/**
 * Client Portal API
 * Client Portal OpenAPI specification
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {Error} from '../models/Error';
import type {GetLoginLinkResponse} from '../models/GetLoginLinkResponse';
import type {InviteUserInfo} from '../models/InviteUserInfo';
import type {User} from '../models/User';
import type {UserShortInfo} from '../models/UserShortInfo';

export interface GenerateLoginLink$Request {
    userId: number;
}

export interface GenerateLoginLink$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const generateLoginLinkRequest = createAction<PayloadData<void, GenerateLoginLink$Request>>('USER_IDENTITY__GENERATE_LOGIN_LINK__REQUEST');
export const generateLoginLinkSuccess = createAction<PayloadData<GetLoginLinkResponse, GenerateLoginLink$Request>>('USER_IDENTITY__GENERATE_LOGIN_LINK__SUCCESS');
export const generateLoginLinkFailure = createAction<PayloadData<Error, GenerateLoginLink$Request, true>>('USER_IDENTITY__GENERATE_LOGIN_LINK__FAILURE');

export const generateLoginLinkActions = [
    generateLoginLinkRequest,
    generateLoginLinkSuccess,
    generateLoginLinkFailure
] as const;

/**
 * @param {number} meta.userId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const generateLoginLink = (meta: GenerateLoginLink$Request, config?: GenerateLoginLink$Config) => {
    return {
        CALL_API: {
            types: generateLoginLinkActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/users/${meta.userId}/login-link/generate`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetCurrentUser$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getCurrentUserRequest = createAction<PayloadData<void>>('USER_IDENTITY__GET_CURRENT_USER__REQUEST');
export const getCurrentUserSuccess = createAction<PayloadData<User>>('USER_IDENTITY__GET_CURRENT_USER__SUCCESS');
export const getCurrentUserFailure = createAction<PayloadData<Error, void, true>>('USER_IDENTITY__GET_CURRENT_USER__FAILURE');

export const getCurrentUserActions = [
    getCurrentUserRequest,
    getCurrentUserSuccess,
    getCurrentUserFailure
] as const;

/**
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getCurrentUser = (config?: GetCurrentUser$Config) => {
    return {
        CALL_API: {
            types: getCurrentUserActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/users/current`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        }
    }
};

export interface GetUserById$Request {
    userId: number;
}

export interface GetUserById$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getUserByIdRequest = createAction<PayloadData<void, GetUserById$Request>>('USER_IDENTITY__GET_USER_BY_ID__REQUEST');
export const getUserByIdSuccess = createAction<PayloadData<User, GetUserById$Request>>('USER_IDENTITY__GET_USER_BY_ID__SUCCESS');
export const getUserByIdFailure = createAction<PayloadData<Error, GetUserById$Request, true>>('USER_IDENTITY__GET_USER_BY_ID__FAILURE');

export const getUserByIdActions = [
    getUserByIdRequest,
    getUserByIdSuccess,
    getUserByIdFailure
] as const;

/**
 * @param {number} meta.userId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getUserById = (meta: GetUserById$Request, config?: GetUserById$Config) => {
    return {
        CALL_API: {
            types: getUserByIdActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/users/${meta.userId}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetUsers$Request {
    requestBody: Array<number>;
}

export interface GetUsers$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getUsersRequest = createAction<PayloadData<void, GetUsers$Request>>('USER_IDENTITY__GET_USERS__REQUEST');
export const getUsersSuccess = createAction<PayloadData<Array<User>, GetUsers$Request>>('USER_IDENTITY__GET_USERS__SUCCESS');
export const getUsersFailure = createAction<PayloadData<Error, GetUsers$Request, true>>('USER_IDENTITY__GET_USERS__FAILURE');

export const getUsersActions = [
    getUsersRequest,
    getUsersSuccess,
    getUsersFailure
] as const;

/**
 * @param {Array<number>} meta.requestBody List of users IDs
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getUsers = (meta: GetUsers$Request, config?: GetUsers$Config) => {
    return {
        CALL_API: {
            types: getUsersActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/users`,
            TRANSPORT_REQUEST_BODY: meta.requestBody,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetUsersByEmails$Request {
    userEmail: string;
}

export interface GetUsersByEmails$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getUsersByEmailsRequest = createAction<PayloadData<void, GetUsersByEmails$Request>>('USER_IDENTITY__GET_USERS_BY_EMAILS__REQUEST');
export const getUsersByEmailsSuccess = createAction<PayloadData<Array<UserShortInfo>, GetUsersByEmails$Request>>('USER_IDENTITY__GET_USERS_BY_EMAILS__SUCCESS');
export const getUsersByEmailsFailure = createAction<PayloadData<Error, GetUsersByEmails$Request, true>>('USER_IDENTITY__GET_USERS_BY_EMAILS__FAILURE');

export const getUsersByEmailsActions = [
    getUsersByEmailsRequest,
    getUsersByEmailsSuccess,
    getUsersByEmailsFailure
] as const;

/**
 * @param {string} meta.userEmail userEmail
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getUsersByEmails = (meta: GetUsersByEmails$Request, config?: GetUsersByEmails$Config) => {
    const TRANSPORT_GET_PARAMS = {
        userEmail: meta.userEmail,
    };
    return {
        CALL_API: {
            types: getUsersByEmailsActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/users/emails/search`,
            TRANSPORT_GET_PARAMS,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface InviteUser$Request {
    inviteUserInfo: InviteUserInfo;
}

export interface InviteUser$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const inviteUserRequest = createAction<PayloadData<void, InviteUser$Request>>('USER_IDENTITY__INVITE_USER__REQUEST');
export const inviteUserSuccess = createAction<PayloadData<User, InviteUser$Request>>('USER_IDENTITY__INVITE_USER__SUCCESS');
export const inviteUserFailure = createAction<PayloadData<Error, InviteUser$Request, true>>('USER_IDENTITY__INVITE_USER__FAILURE');

export const inviteUserActions = [
    inviteUserRequest,
    inviteUserSuccess,
    inviteUserFailure
] as const;

/**
 * @param {InviteUserInfo} meta.inviteUserInfo User invite info
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const inviteUser = (meta: InviteUser$Request, config?: InviteUser$Config) => {
    return {
        CALL_API: {
            types: inviteUserActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/users`,
            TRANSPORT_REQUEST_BODY: meta.inviteUserInfo,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface SendLoginLink$Request {
    userId: number;
}

export interface SendLoginLink$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const sendLoginLinkRequest = createAction<PayloadData<void, SendLoginLink$Request>>('USER_IDENTITY__SEND_LOGIN_LINK__REQUEST');
export const sendLoginLinkSuccess = createAction<PayloadData<void, SendLoginLink$Request>>('USER_IDENTITY__SEND_LOGIN_LINK__SUCCESS');
export const sendLoginLinkFailure = createAction<PayloadData<Error, SendLoginLink$Request, true>>('USER_IDENTITY__SEND_LOGIN_LINK__FAILURE');

export const sendLoginLinkActions = [
    sendLoginLinkRequest,
    sendLoginLinkSuccess,
    sendLoginLinkFailure
] as const;

/**
 * @param {number} meta.userId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const sendLoginLink = (meta: SendLoginLink$Request, config?: SendLoginLink$Config) => {
    return {
        CALL_API: {
            types: sendLoginLinkActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/users/${meta.userId}/login-link`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UninviteUserById$Request {
    userId: number;
}

export interface UninviteUserById$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const uninviteUserByIdRequest = createAction<PayloadData<void, UninviteUserById$Request>>('USER_IDENTITY__UNINVITE_USER_BY_ID__REQUEST');
export const uninviteUserByIdSuccess = createAction<PayloadData<void, UninviteUserById$Request>>('USER_IDENTITY__UNINVITE_USER_BY_ID__SUCCESS');
export const uninviteUserByIdFailure = createAction<PayloadData<Error, UninviteUserById$Request, true>>('USER_IDENTITY__UNINVITE_USER_BY_ID__FAILURE');

export const uninviteUserByIdActions = [
    uninviteUserByIdRequest,
    uninviteUserByIdSuccess,
    uninviteUserByIdFailure
] as const;

/**
 * @param {number} meta.userId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const uninviteUserById = (meta: UninviteUserById$Request, config?: UninviteUserById$Config) => {
    return {
        CALL_API: {
            types: uninviteUserByIdActions,
            method: ApiMethods.DELETE,
            endpoint: `/web-api/users/${meta.userId}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

